import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Loader2,
  Lock,
  Mic,
  Paperclip,
  Printer,
  RotateCcw,
  Send,
  Sparkles,
} from "lucide-react";
import markdownit from "markdown-it";
import { Page } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { ContentCard } from "@/components/ui/ContentCard";
import { Textarea } from "@/components/ui/textarea";
import AIleafGold from "@/components/ui/AIleafGold.svg";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useSpeechDictation } from "@/hooks/use-speech-dictation";
import {
  openDocumentationPrintWindow,
  saveDecision,
  setDecisionInResources,
} from "@/services/savedDecisionsService";
import { storageService } from "@/services/storageService";
import "@/components/pages/ai-mentor/components/html-default-styles.css";

const md = markdownit({ html: true, linkify: true, typographer: true });

/**
 * Four conversation-driven stages. Unlike the Behavior Incident Toolkit (where
 * the user clicks each step), the Strategic page **advances itself** as the
 * assistant earns the next stage:
 *
 *   - Tension  → Evidence: assistant cites an effect size or names a research
 *                source (Hattie / Marzano / Schmoker / etc.) — or the user
 *                taps "Surface evidence-based strategies".
 *   - Evidence → Plan:     assistant produces a 30/60/90 plan structure — or
 *                the user taps "Build my implementation plan".
 *   - Plan     → Documentation: user explicitly asks to package the conversation
 *                into documentation (terminal step).
 */
const STAGES = [
  {
    id: "tension",
    label: "The Tension",
    micro: "Frame the goal & constraint",
  },
  {
    id: "evidence",
    label: "The Evidence",
    micro: "Strategies & effect sizes",
  },
  {
    id: "plan",
    label: "The Plan",
    micro: "30 / 60 / 90 implementation",
  },
  {
    id: "documentation",
    label: "The Documentation",
    micro: "Save, export & revisit",
  },
] as const;

type StageIndex = 0 | 1 | 2 | 3;

const WELCOME_HTML = `
<div class="space-y-3">
<p>You're in the <strong>Strategic Decision Partner</strong>. Share what you're weighing — goals, tensions, and constraints — and we'll work through it step by step: frame the tension, surface evidence, build a plan, then save documentation.</p>
<p>I'll ask <strong>one or two focused questions at a time</strong>, share a brief read as we go, and only build the full plan and documentation once we're aligned on direction.</p>
<p><strong>What decision are you working through?</strong></p>
</div>
`;

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  /** HTML for assistant messages, plain text for user messages. */
  content: string;
  timestamp: Date;
  /** Original markdown returned by the assistant; used for the saved doc. */
  rawMarkdown?: string;
}

const STAGE_CTA: Record<
  StageIndex,
  { primary: { label: string; prompt: string } | null; chips: { label: string; prompt: string }[] }
> = {
  0: {
    primary: {
      label: "Surface evidence-based strategies",
      prompt:
        "Based on what I've shared, surface 2–3 evidence-based strategies that fit my context. For each: name, effect size with source, and one sentence on fit. Keep the whole reply under 200 words. Ask at most one follow-up question if you still need it.",
    },
    chips: [
      {
        label: "Ask me one more question",
        prompt:
          "Ask me your single most important clarifying question — one question only, under 80 words total.",
      },
      {
        label: "Reframe the tension",
        prompt:
          "Help me reframe the tension in one cleaner sentence I could say out loud to a peer. Under 60 words; no questions unless one is essential.",
      },
    ],
  },
  1: {
    primary: {
      label: "Build my implementation plan",
      prompt:
        "Now build me an implementation plan around the strongest strategy fit: 30 / 60 / 90 day moves, the 2–3 people I need on board (and what I need from each), one leading indicator I'll watch each month, and the most likely failure mode I should design against.",
    },
    chips: [
      {
        label: "Compare effect sizes",
        prompt:
          "Compare the effect sizes of the leading strategies and rank them. Note any caveats around how the effect size translates to my setting.",
      },
      {
        label: "Show caveats",
        prompt:
          "What caveats or contextual mismatches should I be aware of for these strategies? Where does the research not transfer cleanly to my situation?",
      },
    ],
  },
  2: {
    primary: {
      label: "Create the documentation",
      prompt: "[CREATE_RESOURCE_DOCUMENT]",
    },
    chips: [
      {
        label: "Tighten this plan",
        prompt:
          "Suggest two ways to tighten or sharpen this plan given my constraints. Be specific about what to cut, condense, or sequence differently.",
      },
      {
        label: "Risks & mitigations",
        prompt:
          "List the top 3 risks of this plan and the mitigation move I'd take for each. Keep each to one sentence.",
      },
      {
        label: "Quotable defense",
        prompt:
          "Draft the language I'd use with my Board or staff to defend this strategic choice — grounded in research. Under 150 words.",
      },
    ],
  },
  3: { primary: null, chips: [] },
};

const DOCUMENTATION_PROMPT = `Now produce a clean, single-page **documentation** write-up of this strategic decision in **Markdown only** — no surrounding commentary, no explanation, just the document.

Structure (use these exact headings):

# [A concise, specific title for this decision]

## The tension
2–3 sentences capturing what was at stake.

## Evidence-based strategies
For each strategy: **name** — effect size with source (Hattie / Marzano / Schmoker / etc.) — one or two sentences on why it fits this context.

## The plan
30 / 60 / 90 day moves. Who needs to be on board. The leading indicator I'll watch.

## Risks & watchouts
Brief bullet list.

## Quotable defense
1–3 sentences I can use with my Board or staff.

Keep it tight, specific to my context, and easy to print.`;

const evidenceSignal = /effect size|hattie|marzano|schmoker|meta-analysis|research-backed|d\s*=\s*\d/i;
const planSignal = /30\s*\/?\s*60\s*\/?\s*90|implementation plan|first 30 days|leading indicator/i;

const StrategicChatRow = forwardRef<
  HTMLDivElement,
  {
    msg: ChatMessage;
    userInitial: string;
  }
>(function StrategicChatRow({ msg, userInitial }, ref) {
  const isUser = msg.role === "user";
  return (
    <div
      ref={ref}
      className={cn(
        "flex scroll-mt-3 items-start space-x-3",
        isUser && "flex-row-reverse space-x-reverse",
      )}
    >
      <div
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-full md:h-8 md:w-8",
          isUser ? "bg-bodhi-teal" : "bg-white",
        )}
      >
        {isUser ? (
          <span className="text-xs font-medium text-white md:text-sm">{userInitial}</span>
        ) : (
          <img src={AIleafGold} alt="" width={24} height={24} className="h-6 w-6 md:h-8 md:w-8" />
        )}
      </div>
      <div className="relative min-w-0 flex-1">
        <div
          className={cn(
            "w-fit max-w-[80%] rounded-2xl p-3 py-2 text-sm md:max-w-[60%] md:p-4 md:text-base lg:min-w-[400px]",
            isUser
              ? "ml-auto rounded-tr-sm bg-bodhi-teal text-white"
              : "rounded-tl-sm bg-gray-100 text-gray-700",
          )}
        >
          {isUser ? (
            <p className="whitespace-pre-wrap">{msg.content}</p>
          ) : (
            <div className="chat-message-content" dangerouslySetInnerHTML={{ __html: msg.content }} />
          )}
          <time className="mt-2 block text-xs opacity-70">
            {msg.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </time>
        </div>
      </div>
    </div>
  );
});

StrategicChatRow.displayName = "StrategicChatRow";

export const StrategicDecisionPage = () => {
  const navigate = useNavigate();
  const { session, userData } = useAuth();

  const [messages, setMessages] = useState<ChatMessage[]>([
    { id: "welcome", role: "assistant", content: WELCOME_HTML, timestamp: new Date() },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [activeStage, setActiveStage] = useState<StageIndex>(0);
  const [stageReached, setStageReached] = useState<[boolean, boolean, boolean, boolean]>([
    true,
    false,
    false,
    false,
  ]);
  const [generatedDocMarkdown, setGeneratedDocMarkdown] = useState<string | null>(null);
  const [savedDecisionId, setSavedDecisionId] = useState<string | null>(null);
  const [isInResources, setIsInResources] = useState(false);

  const speech = useSpeechDictation((text) => {
    setInput((prev) => (prev.trim() ? `${prev} ${text}` : text));
  });
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  const loadingRowRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesRef = useRef<ChatMessage[]>([]);

  messagesRef.current = messages;

  const userInitial = userData?.user_name?.trim()?.[0]?.toUpperCase() ?? "Y";

  const userMessageCount = useMemo(
    () => messages.filter((m) => m.role === "user").length,
    [messages],
  );

  const reachedCount = useMemo(() => stageReached.filter(Boolean).length, [stageReached]);

  const progressPercent = useMemo(() => {
    const reachedFraction = (reachedCount - 1) / (STAGES.length - 1);
    const stageBase = Math.round(reachedFraction * 90);
    const messageBoost = Math.min(10, userMessageCount * 2);
    return Math.min(100, stageBase + messageBoost);
  }, [reachedCount, userMessageCount]);

  /**
   * Pinning `scrollTop = scrollHeight` showed the *bottom* of the thread. Long
   * assistant bubbles then hid their tops — users had to scroll up to read.
   * Instead, scroll the latest row into view with `block: "start"` so the
   * beginning of each reply is visible first.
   */
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const target = isLoading ? loadingRowRef.current : lastMessageRef.current;
        target?.scrollIntoView({ block: "start", behavior: "auto" });
      });
    });
    return () => cancelAnimationFrame(id);
  }, [messages, isLoading]);

  const advanceStage = useCallback((target: StageIndex) => {
    setActiveStage((current) => (target > current ? target : current));
    setStageReached((prev) => {
      const next = [...prev] as [boolean, boolean, boolean, boolean];
      for (let i = 0; i <= target; i++) next[i] = true;
      return next;
    });
  }, []);


  /**
   * Build the message array we send to Anthropic. Claude is stateless, so the
   * client owns the transcript: every turn we send the full history as plain
   * text. Assistant messages are stored as HTML for rendering, so we use
   * `rawMarkdown` when present and fall back to a stripped version of the HTML.
   */
  const buildOutgoingMessages = useCallback(
    (nextUserText: string): { role: "user" | "assistant"; content: string }[] => {
      const history = messagesRef.current
        .filter((m) => m.id !== "welcome")
        .map((m) => {
          if (m.role === "user") return { role: "user" as const, content: m.content };
          const text = m.rawMarkdown ?? m.content.replace(/<[^>]+>/g, "").trim();
          return { role: "assistant" as const, content: text };
        })
        .filter((m) => m.content.trim().length > 0);

      return [...history, { role: "user", content: nextUserText }];
    },
    [],
  );

  const sendMessage = useCallback(
    async (
      raw: string,
      options: {
        showAsUser: boolean;
        visibleLabel?: string;
        isDocRequest?: boolean;
        responseScope?: "conversational" | "structured";
      },
    ) => {
      const { showAsUser, visibleLabel, isDocRequest, responseScope = "conversational" } = options;

      const outgoing = buildOutgoingMessages(raw);

      if (showAsUser) {
        setMessages((prev) => [
          ...prev,
          {
            id: `u-${Date.now()}`,
            role: "user",
            content: visibleLabel ?? raw,
            timestamp: new Date(),
          },
        ]);
      }
      setIsLoading(true);
      if (isDocRequest) setIsGeneratingDoc(true);
      setError(null);

      try {
        if (!session?.access_token) {
          throw new Error("Sign in to use the Strategic Decision Partner.");
        }

        const { data, error: fnError } = await supabase.functions.invoke("strategic-decision-chat", {
          body: {
            messages: outgoing,
            mode: isDocRequest ? "resource_document" : "chat",
            response_scope: isDocRequest ? undefined : responseScope,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (fnError) {
          const msg = fnError.message || "Failed to reach AI service";
          const isNon2xx = /non-2xx|2xx status|FunctionsHttpError/i.test(msg);
          throw new Error(isNon2xx ? "AI service unavailable. Try again in a moment." : msg);
        }

        if (data?.error) {
          throw new Error(data.error);
        }

        const rawMarkdown = (data?.content as string | undefined) ?? "";
        const html = md.render(rawMarkdown || "No response received.");

        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: "assistant",
            content: html,
            rawMarkdown,
            timestamp: new Date(),
          },
        ]);

        if (isDocRequest) {
          if (rawMarkdown.trim()) {
            setGeneratedDocMarkdown(rawMarkdown);
            advanceStage(3);

            // Auto-save the moment the document exists, so it always lands in
            // History. The user opts into surfacing it in Resources separately.
            const firstUserMessage = messagesRef.current.find((m) => m.role === "user")?.content;
            if (!userData?.user_id) {
              toast.error("Sign in to save this decision.");
              return;
            }
            const decision = saveDecision(userData.user_id, {
              markdown: rawMarkdown,
              firstUserMessage,
              addedToResources: false,
            });
            setSavedDecisionId(decision.id);
            setIsInResources(false);
            toast.success("Saved to History.", {
              description: "Find it in History → Strategic Decision Documents.",
            });
          } else {
            setError("The assistant didn't produce documentation. Try again or ask for the documentation directly.");
          }
        } else {
          if (evidenceSignal.test(rawMarkdown)) advanceStage(1);
          if (planSignal.test(rawMarkdown)) advanceStage(2);
        }
      } catch (err) {
        const errMsg = (err as Error)?.message || "Connection failed";
        setError(errMsg);
      } finally {
        setIsLoading(false);
        if (isDocRequest) setIsGeneratingDoc(false);
      }
    },
    [session?.access_token, advanceStage, buildOutgoingMessages],
  );

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    setInput("");
    if (userMessageCount + 1 >= 3) advanceStage(1);
    void sendMessage(trimmed, { showAsUser: true });
  };

  const handleReset = () => {
    setMessages([{ id: "welcome", role: "assistant", content: WELCOME_HTML, timestamp: new Date() }]);
    setError(null);
    setActiveStage(0);
    setStageReached([true, false, false, false]);
    setGeneratedDocMarkdown(null);
    setSavedDecisionId(null);
    setIsInResources(false);
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!session?.access_token) {
      toast.error("Sign in to upload documents.");
      return;
    }

    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF, Word document, or text file.");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File must be under 20MB.");
      return;
    }

    setIsUploading(true);
    try {
      const uploaded = await storageService.uploadFile(file, "document");
      toast.success(`Saved "${uploaded.file_name}" to your document library.`);
    } catch (err) {
      toast.error((err as Error)?.message || "Upload failed");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleStageJump = (target: StageIndex) => {
    if (!stageReached[target]) return;
    if (target === activeStage) return;
    setActiveStage(target);
  };

  const handleStageCtaClick = (cta: { label: string; prompt: string }) => {
    if (cta.prompt === "[CREATE_RESOURCE_DOCUMENT]") {
      void sendMessage(DOCUMENTATION_PROMPT, {
        showAsUser: true,
        visibleLabel: "Create the documentation.",
        isDocRequest: true,
      });
      return;
    }
    void sendMessage(cta.prompt, {
      showAsUser: true,
      visibleLabel: cta.label,
      responseScope: "structured",
    });
  };

  const handleToggleAddToResources = () => {
    if (!savedDecisionId || !userData?.user_id) return;
    const next = !isInResources;
    const updated = setDecisionInResources(userData.user_id, savedDecisionId, next);
    if (!updated) {
      toast.error("Couldn't update the decision. Try again.");
      return;
    }
    setIsInResources(next);
    if (next) {
      toast.success("Added to My Resources.", {
        action: {
          label: "Open Resources",
          onClick: () => navigate("/resources"),
        },
      });
    } else {
      toast.success("Removed from My Resources. (Still saved in History.)");
    }
  };

  const handlePrintSavePdf = () => {
    if (!generatedDocMarkdown) return;
    const firstUserMessage = messagesRef.current.find((m) => m.role === "user")?.content;
    const ok = openDocumentationPrintWindow(generatedDocMarkdown, firstUserMessage);
    if (!ok) {
      toast.error("Allow pop-ups for this site to print or save as PDF.");
    }
  };

  const isInputDisabled = isLoading || isUploading;
  const stageCta = STAGE_CTA[activeStage];
  const inDocumentationStage = activeStage === 3;

  /**
   * Stage 0 lives entirely in the chat — no "suggested next move" or chips
   * until the user has actually shared enough for the assistant to be working
   * with real context. Stages 1+ are only reached after substantive
   * interaction, so their CTAs are always appropriate.
   */
  const STAGE_0_MIN_USER_MESSAGES = 2;
  const ctasReady = activeStage > 0 || userMessageCount >= STAGE_0_MIN_USER_MESSAGES;

  const derivedSubtitle = useMemo(() => {
    const userMsgs = messages.filter((m) => m.role === "user");
    if (userMsgs.length === 0) return null;
    const combined = userMsgs
      .map((m) => m.content.trim())
      .join(" ")
      .replace(/\s+/g, " ");
    if (!combined) return null;
    return combined.length > 160 ? `${combined.slice(0, 157)}…` : combined;
  }, [messages]);

  return (
    <Page.Container>
      <Page.Header className="space-y-0 pb-2 md:pb-2 md:pt-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="font-literata text-xl font-semibold text-white md:text-2xl">
              Strategic Decision Partner
            </h1>
            <p className="mt-0.5 line-clamp-1 max-w-3xl font-inter text-xs text-white/70 md:text-sm">
              {derivedSubtitle ??
                "Frame the tension → surface evidence → build a 30/60/90 plan → save the documentation."}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="shrink-0 text-white/75 hover:bg-white/10 hover:text-white"
          >
            <RotateCcw className="mr-1 h-3.5 w-3.5" />
            Reset
          </Button>
        </div>
      </Page.Header>

      <Page.Content hideFooter className="flex min-h-0 flex-col flex-1 pt-1 md:pt-2">
        <div className="flex min-h-0 flex-1 flex-col gap-2 !space-y-0 md:gap-3">

        <ContentCard className="flex min-h-0 flex-1 flex-col overflow-hidden border-2 border-bodhi-gold/40 bg-white p-0 shadow-md">
          {/* Compact in-card breadcrumb pills + progress — single source of "where you are". */}
          <div className="shrink-0 border-b border-stone-200 bg-stone-50/70 px-3 py-2 md:px-5 md:py-2.5">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              {STAGES.map((stage, i) => {
                const isActive = i === activeStage;
                const isReached = stageReached[i];
                const isLocked = !isReached;
                return (
                  <div key={stage.id} className="flex items-center gap-1.5">
                    <button
                      type="button"
                      disabled={isLocked || isActive}
                      onClick={() => handleStageJump(i as StageIndex)}
                      className={cn(
                        "flex items-center gap-1.5 rounded-full px-1.5 py-0.5 font-inter text-[11px] transition-colors md:text-xs",
                        isActive
                          ? "text-bodhi-teal font-semibold"
                          : isReached
                            ? "text-stone-700 hover:text-bodhi-teal"
                            : "cursor-not-allowed text-stone-400",
                      )}
                    >
                      <span
                        className={cn(
                          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full",
                          isActive
                            ? "bg-bodhi-teal text-white"
                            : isReached
                              ? "border border-bodhi-teal/40 bg-white text-bodhi-teal"
                              : "border border-stone-200 bg-white text-stone-400",
                        )}
                        aria-hidden
                      >
                        {isLocked ? <Lock className="h-2.5 w-2.5" /> : <span className="block h-1.5 w-1.5 rounded-full bg-current" />}
                      </span>
                      <span>{stage.label}</span>
                    </button>
                    {i < STAGES.length - 1 && (
                      <ArrowRight className="h-3 w-3 shrink-0 text-stone-300" aria-hidden />
                    )}
                  </div>
                );
              })}
              <div className="ml-auto flex min-w-[120px] flex-1 items-center gap-2">
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-stone-200">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-bodhi-teal to-bodhi-gold transition-[width] duration-300"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <span className="font-inter text-[10px] font-medium text-stone-500 tabular-nums md:text-[11px]">
                  {progressPercent}%
                </span>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex shrink-0 items-center gap-2 border-b border-amber-200/40 bg-amber-50 px-4 py-2 md:px-6">
              <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
              <p className="font-inter text-xs text-amber-800">{error}</p>
            </div>
          )}

          <div
            ref={scrollRef}
            className="relative min-h-0 flex-1 basis-0 space-y-4 overflow-y-auto overscroll-y-contain px-4 pt-2 md:px-6 md:pt-3 [-webkit-overflow-scrolling:touch]"
          >
            {messages.map((msg, index) => (
              <StrategicChatRow
                key={msg.id}
                ref={index === messages.length - 1 ? lastMessageRef : undefined}
                msg={msg}
                userInitial={userInitial}
              />
            ))}

            {isLoading && (
              <div ref={loadingRowRef} className="flex scroll-mt-3 items-start space-x-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white md:h-8 md:w-8">
                  <img src={AIleafGold} alt="" width={24} height={24} className="h-6 w-6 md:h-8 md:w-8" />
                </div>
                <div className="relative min-w-0 flex-1">
                  <div className="w-fit max-w-[80%] rounded-2xl rounded-tl-sm bg-gray-100 p-3 py-2 text-sm text-gray-700 md:max-w-[60%] md:p-4 md:text-base lg:min-w-[400px]">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />
                      <span>
                        {isGeneratingDoc ? "Building your documentation…" : "Searching research…"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-200 bg-gray-50/40">
            {!isLoading && ctasReady && stageCta.primary && (
              <div className="flex flex-wrap items-center gap-2 px-4 pt-3 md:px-6">
                <span className="font-inter text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                  Next move
                </span>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => handleStageCtaClick(stageCta.primary!)}
                  className="ml-auto gap-2 bg-bodhi-teal text-white hover:bg-bodhi-teal/90"
                >
                  {stageCta.primary.label}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                </Button>
              </div>
            )}

            {!isLoading && ctasReady && stageCta.chips.length > 0 && (
              <div className="flex flex-wrap gap-1.5 px-4 pt-2 md:px-6">
                {stageCta.chips.map((chip) => (
                  <button
                    key={chip.label}
                    type="button"
                    onClick={() => handleStageCtaClick(chip)}
                    className="rounded-full border border-gray-200 bg-white px-2.5 py-1 font-inter text-[11px] font-medium text-gray-600 transition hover:border-bodhi-teal/40 hover:text-bodhi-teal"
                  >
                    {chip.label}
                  </button>
                ))}
              </div>
            )}

            <div className="px-4 pb-4 pt-3 md:px-6">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                className="hidden"
                onChange={(e) => void handleFileChosen(e)}
              />
              <div className="flex items-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleAttachClick}
                  disabled={isInputDisabled}
                  className="h-11 w-11 shrink-0 border-gray-200 text-bodhi-teal hover:bg-gray-50"
                  aria-label="Attach a document"
                >
                  {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    if (!speech.isSupported) {
                      toast.error("Voice dictation is not supported in this browser.");
                      return;
                    }
                    speech.toggleListening();
                  }}
                  disabled={isInputDisabled}
                  title={speech.isListening ? "Stop dictation" : "Dictate with your voice"}
                  aria-pressed={speech.isListening}
                  aria-label={speech.isListening ? "Stop voice dictation" : "Start voice dictation"}
                  className={cn(
                    "h-11 w-11 shrink-0 border-gray-200 text-bodhi-teal hover:bg-gray-50",
                    speech.isListening && "border-bodhi-teal bg-bodhi-teal/10",
                  )}
                >
                  {speech.isListening ? (
                    <span className="relative flex h-3 w-3">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex h-3 w-3 rounded-full bg-red-500" />
                    </span>
                  ) : (
                    <Mic className="h-4 w-4" />
                  )}
                </Button>
                <Textarea
                  id="strategic-decision-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={
                    activeStage === 0
                      ? "Type the decision you're weighing…"
                      : "Reply, ask, or push back…"
                  }
                  className="min-h-[44px] max-h-[140px] min-w-0 flex-1 resize-none rounded-xl border-gray-200 bg-white text-sm shadow-inner"
                  rows={2}
                  disabled={isInputDisabled}
                />
                <Button
                  type="button"
                  size="icon"
                  onClick={handleSend}
                  disabled={!input.trim() || isInputDisabled}
                  className="h-11 w-11 shrink-0 bg-bodhi-teal text-white hover:bg-bodhi-teal/90"
                  aria-label="Send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </ContentCard>

        {inDocumentationStage && generatedDocMarkdown && (
          <div className="shrink-0 space-y-2.5 rounded-2xl border border-bodhi-gold/40 bg-white/95 px-4 py-3 shadow-sm md:px-5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="font-literata text-sm font-semibold text-bodhi-teal md:text-base">
                Your documentation is ready
              </p>
              {savedDecisionId && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-inter text-[11px] font-medium text-emerald-800 ring-1 ring-emerald-200">
                  <CheckCircle2 className="h-3 w-3" aria-hidden /> Saved to History
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                onClick={handleToggleAddToResources}
                disabled={!savedDecisionId}
                className={cn(
                  "gap-2",
                  isInResources
                    ? "border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
                    : "bg-bodhi-teal text-white hover:bg-bodhi-teal/90",
                )}
              >
                {isInResources ? (
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <Sparkles className="h-3.5 w-3.5" aria-hidden />
                )}
                {isInResources ? "Added to My Resources" : "Add to My Resources"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handlePrintSavePdf}
                className="gap-2 border-gray-200 text-bodhi-teal hover:bg-gray-50"
              >
                <Printer className="h-3.5 w-3.5" aria-hidden />
                Save as PDF
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => navigate("/history")}
                className="gap-2 text-bodhi-teal hover:bg-gray-50"
              >
                <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                Open in History
              </Button>
            </div>
          </div>
        )}

        </div>
      </Page.Content>
    </Page.Container>
  );
};
